import React, { useEffect, useState } from 'react';
import { Button, Text, View } from "../../src/Components/Themed"
import * as Font from 'expo-font';
import { APIOverrides, Authenticator, BracketRoom } from 'be-components';
import type { AuthStateProps } from '../../src/ApiOverrides/types';
import ShareWidget from '../../src/Share';
import { Image, Modal, useWindowDimensions } from 'react-native';
import type { BracketCompetitionProps, CompanyProps, CompetitionPlayerBracketProps, MyPlayerProps, PlayerBracketProps } from '../../src/types';
import { BracketApi, BracketComeptitionApi } from '../../src/Bracket/api';
import { CompanyProfileApi } from '../../src/SocialComponents/CompanyProfile/api';
import { useColors } from '../../src/constants/useColors';
import type { AuthenticatedProps } from '../../src/Authenticator/api/types';
import RoundEventStats from './components/RoundEventModal';

type BracketEmbedProps = {
    bracket_competition_id?:string,
    insets: { top:number, left:number, right:number, bottom:number }
    refer?:string
}

const BracketEmbed = ({ bracket_competition_id, refer, insets } : BracketEmbedProps) => {
    const Colors = useColors();
    const { width, height } = useWindowDimensions();
    const [ re_data, setREData ] = useState<any>({
        visible: false,
    })
    const [ embed_size, setEmbedSize ] = useState({ width:0, height:0 });
    const [ play_state, setPlayState ] = useState<{
        play_loading: boolean,
        complete_prompted?:boolean,
        bracket_competition?:BracketCompetitionProps,
        company?:CompanyProps,
        player_bracket?:PlayerBracketProps,
        competition_player_bracket?:CompetitionPlayerBracketProps
    }>({
        play_loading: false,
        complete_prompted: false
    });
    const { play_loading, complete_prompted, player_bracket, bracket_competition, company, competition_player_bracket } = play_state;
    const [ embed_state, setEmbedState ] = useState<AuthStateProps & { loaded:boolean, launched?:boolean }>({
        authenticated: false,
        distinct_id: '',
        session_id: '',
        loaded: false,
        launched: false
    })
    const { loaded, launched } = embed_state;
    const [ authenticate, setAuthenticate ] = useState<{
        visible: boolean,
        auth_strategy_id:string,
        opt_in_url?:string
    }>({
        visible: false,
        auth_strategy_id:'3'
    });
    const [ share, setShare ] = useState<{
        visible:boolean,
        share?:any
    }>({
        visible:false,
    });

    const [font_loaded, error] = Font.useFonts({
        BarlowBold: require('../assets/fonts/Barlow-Bold.ttf'),
        BarlowSemiBold: require('../assets/fonts/Barlow-SemiBold.ttf'),
        BarlowRegular: require('../assets/fonts/Barlow-Regular.ttf'),
        BarlowLight: require('../assets/fonts/Barlow-Light.ttf'),
      });

    useEffect(() => {
        startUp()
    },[]);

    const startUp = async() => {
        const auth_state = await APIOverrides.start({
            env: 'PRODUCTION',
            no_cache:false
        });
        CompanyProfileApi.setEnvironment();
        BracketApi.setEnvironment();
        setEmbedState({
            ...auth_state,
            loaded: true,
            launched: launched
        });
    }


    useEffect(() => {
        if(!embed_state.loaded){ return }
        if(!bracket_competition_id){ return }
        getPlayData(bracket_competition_id)
    },[embed_state.loaded, bracket_competition_id, embed_state.player_id])

    const getPlayData = async(id:string) => {
        setPlayState({ ...play_state, play_loading: true });
        const bc = await BracketComeptitionApi.getBracketCompetitionById(id);
        let c:CompanyProps|undefined = undefined
        if(bc?.bracket_competition.company_id){
            const c_resp = await CompanyProfileApi.getCompanyById(bc.bracket_competition.company_id);
            c = c_resp?.company
        }
        let my_cpb:CompetitionPlayerBracketProps | undefined = undefined;
        let my_pb:PlayerBracketProps | undefined = undefined;
        if(embed_state.player_id && bc?.bracket_competition){
            const my_data = await BracketComeptitionApi.getMyPlayerBrackets(bc.bracket_competition.bracket_id);
            my_cpb = my_data.competition_player_brackets.find(cpb => cpb.status == 'active' && cpb.bracket_competition_id == bc.bracket_competition.bracket_competition_id);
            if(my_cpb){
                const pb_resp = await BracketComeptitionApi.getPlayerBracketById(my_cpb.player_bracket_id);
                my_pb = pb_resp.player_bracket
            }
        }
        setPlayState({
            ...play_state,
            play_loading: false,
            company: c,
            bracket_competition: bc?.bracket_competition,
            competition_player_bracket: my_cpb,
            player_bracket: my_pb
        })
    }

    const handleOnComplete = (player:MyPlayerProps, pb:PlayerBracketProps, bc?:BracketCompetitionProps, cpb?:CompetitionPlayerBracketProps) => {
        if(complete_prompted){ return } //Only prompt 
        if(!cpb){ return } //This shoudl prompt for a competition in there!
    }


    if(!loaded){ return <></> }

    const MAX_IMG_SIZE = 100
    const image_size = embed_size.height > MAX_IMG_SIZE ? MAX_IMG_SIZE : embed_size.height -3
    if(!launched){
        if(!bracket_competition){ return <></> }
        return (
            <View transparent nativeID='buttonbanner' style={{ flex:1, padding:10 }} >
                <Button float style={{ flex:1, flexDirection:'row', padding:0 }} onLayout={(ev) => {
                    const { width, height } = ev.nativeEvent.layout;
                    setEmbedSize({ height, width })
                }}
                onPress={() => setEmbedState({ ...embed_state, launched: true })}>
                    <Image
                        source={{ uri: bracket_competition.image?.url}}
                        style={{ height:image_size, width:image_size, borderTopLeftRadius:8, borderBottomLeftRadius:8 }}
                        resizeMode='cover'
                    />
                    <View transparent style={{ flex:1, borderTopRightRadius:8, borderBottomRightRadius:8 }}>
                        <View type='header' style={{ padding:10 }}>
                            <Text theme='h1'>{bracket_competition.competition_name}</Text>
                        </View>
                        <View transparent style={{ flex:1, flexDirection:'row', alignItems:'center', padding:10 }}>
                            {competition_player_bracket && player_bracket ?
                            <View transparent style={{ flex:1 }}>
                                <Text theme='description' style={{ marginTop:3 }}>{player_bracket.bracket_name}</Text>
                                <Text style={{ marginTop:3 }} theme='description'>{competition_player_bracket.points_scored} Points (Place {competition_player_bracket.place} / {bracket_competition.tickets_sold})</Text>
                            </View>
                            :
                            <View transparent style={{ flex:1 }}>
                                <Text theme='description' style={{ marginTop:3 }}>{bracket_competition.competition_description}</Text>
                                <Text style={{ marginTop:3 }} theme='description'>Powered By BettorEdge</Text>
                            </View>
                            }
                            <Button
                                title={competition_player_bracket ? 'View Status' : 'Play Now!'}
                                disabled
                                style={{ padding:10 }}
                                type='success'  
                            />
                        </View>
                    </View>
                </Button>
            </View>
        )
    }
    const view_mode = width > 600 ? 'desktop' : 'mobile'
    const auth_width = width > 600 ? 500: width - 20
    return (
        <Modal
            transparent
            
            animationType={view_mode == 'desktop' ? 'fade' : 'slide'}
            style={{ flex:1 }}
        >
            <View float style={{ flex:1, marginTop: insets.top, marginBottom: insets.bottom }}>
            {bracket_competition ?
            <Button 
                type='action' 
                style={{ flexDirection:'row', alignItems:'center', padding:0, paddingRight:10, borderRadius:0, borderTopLeftRadius:8, borderTopRightRadius:8 }}
                onPress={() => setEmbedState({ ...embed_state, launched: false })}>
                <Image
                    source={{ uri: bracket_competition.image?.url}}
                    style={{ height:45, width:45, borderTopLeftRadius:8 }}
                    resizeMode='cover'
                />
                <View transparent style={{ flex:1, padding:10 }}>
                    <Text theme='h2' color={Colors.text.white}>Playing Bracket Challenge</Text>
                </View>
                <Button
                    title='X'
                    float
                    disabled
                    transparent
                    title_color={Colors.text.white}
                    style={{ margin:5, padding:0, height:30, width:30, borderRadius:100, justifyContent:'center', alignItems:'center' }}
                    onPress={() => setEmbedState({ ...embed_state, launched: false })}
                />
            </Button>
            :<></>}
            <BracketRoom
                player_id={embed_state.player_id as string | undefined}
                bracket_competition_id={bracket_competition_id}
                onManageCompetition={() => console.log('')}
                hide_home
                onCompleteBracket={(player, pb, bc, cpb) => handleOnComplete(player, pb, bc, cpb)}
                insets={insets}
                restrict_competition
                onRequestAuthenticate={(auth_strategy_id, opt_in_url) => {
                    setAuthenticate({
                        visible: true,
                        auth_strategy_id: auth_strategy_id ?? '3',
                        opt_in_url
                    });
                }}
                onSelectContest={(re, bk, pbp, app, hpp) => {
                    setREData({
                        visible: true,
                        round_event:re,
                        bracket:bk,
                        player_bracket_pick: pbp,
                        away_placeholder_pick: app,
                        home_placeholder_pik: hpp
                    })
                }}
                onNotEnoughBalance={() => console.log('')}
                onShareCompetition={(c) => setShare({
                    visible: true,
                    share: {
                        title: `Bracket Competition`,
                        body: 'Compete against me on BettorEdge Brackets!',
                        type_id:c.bracket_competition_id,
                        generator:'player',
                        link_type: 'user_generated_link',
                        link_sub_type: 'bracket_link',
                        redirect_url: `https://play.bettoredge.com/competitions/bracket/${c.bracket_competition_id}`
                    }
                })}
                view_mode={view_mode}
                width={width}
                height={height}
            />
            {authenticate.visible ?
            <View type='blur' style={{ position:'absolute', top:0, left:0, right:0, bottom:0, justifyContent:width > 600 ? 'center' : 'flex-end', alignItems:'center'}}>
                <View float style={{ margin:10, width:auth_width }}>
                    <Authenticator
                        company_id={company?.company_id ?? '1'}
                        auth_strategy_id={authenticate.auth_strategy_id}
                        onAuthenticated={async(auth_state:AuthenticatedProps) => {
                            await APIOverrides.authenticateApp({
                                ...auth_state,
                                distinct_id: embed_state.distinct_id,
                                session_id: embed_state.session_id,
                                authenticated: true     
                            }, { cache: true })
                            setEmbedState({
                                ...auth_state,
                                loaded: true,
                                authenticated: true,
                                launched: true,
                                distinct_id: embed_state.distinct_id,
                                session_id: embed_state.session_id
                            })
                            
                            setAuthenticate({ ...authenticate, visible: false });
                        }}
                        onClose={() => setAuthenticate({ ...authenticate, visible: false })}
                        source='embed'
                        referral_code={refer && refer != 'undefined' ? refer : 'tipoff'}
                        opt_in_url={authenticate.opt_in_url}
                    />
                </View>
            </View>
            :<></>}
            {re_data.visible ? 
            <View transparent style={{ position:'absolute', top:0, left:0, right:0, bottom:0}}>
                <RoundEventStats
                    bracket={re_data.bracket}
                    visible={re_data.visible}
                    player_bracket_pick={re_data.player_bracket_pick}
                    onClose={() => setREData({ visible:false })}
                    mode={view_mode}
                    round_event={re_data.round_event}
                />
            </View>
            :<></>}
            {share.visible ?
            <View type='blur' style={{ position:'absolute', top:0, left:0, right:0, bottom:0, justifyContent:width > 600 ? 'center' : 'flex-end', alignItems:'center'}}>
                <View float style={{ margin:10, width:auth_width }}>
                    <ShareWidget
                        player_id={embed_state.player_id as string | undefined}
                        title={share.share.title}
                        body={share.share.body}
                        redirect_url={share.share.redirect_url}
                        generator={share.share.generator}
                        link_type={share.share.link_type}
                        onSelectContact={() => console.log('')}
                        link_sub_type={share.share.link_sub_type}
                        onCancel={() => setShare({ visible: false })}
                        onShare={() => setShare({ visible: false })}
                    />
                </View>
            </View>
            :<></>}
            </View>

        </Modal>
    )
}

export default BracketEmbed